<template>
  <div class="ctc-articles">
    <v-card flat class="rmr-table-shell ctc-articles__shell">
      <rmr-table-header>
        <template #heading>
          <div>
            <div class="rmr-table-shell__title">
              Brouillons
              <span class="ctc-articles__count">{{ total }}</span>
            </div>
            <div class="ctc-articles__heading-help">
              Brouillons RMR, contenus Affilizz et demande de génération.
            </div>
          </div>
        </template>
        <template #controls>
          <div class="rmr-table-shell__filter-item rmr-table-shell__search">
            <label class="rmr-table-shell__filter-label">Recherche</label>
            <rmr-text-field
              v-model="search"
              :append-icon="icons.mdiMagnify"
              label="Produit, marchand ou site"
              single-line
              clearable
            />
          </div>
        </template>
      </rmr-table-header>

      <v-card-text class="rmr-table-shell__body">
        <v-data-table
          :headers="headers"
          :items="rows"
          :loading="loading"
          :item-class="rowClass"
          :options="options"
          @update:options="setOptions"
          :server-items-length="total"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          loading-text="Chargement des brouillons…"
          class="rmr-data-table ctc-articles__table"
        >
          <template v-slot:item.title="{ item }">
            <div class="ctc-articles__article-copy">
              <div class="ctc-articles__title">{{ item.title }}</div>
              <div class="ctc-articles__product">
                {{ item.merchant }} · Signature : {{ item.signature }}
              </div>
            </div>
          </template>

          <template v-slot:item.siteName="{ item }">
            <div class="ctc-articles__site">{{ item.siteName }}</div>
            <div class="ctc-articles__muted">{{ item.siteDomain }}</div>
          </template>

          <template v-slot:item.postStatus="{ item }">
            <div class="ctc-articles__workflow">
              <span class="ctc-articles__site">{{ item.postStatusLabel }}</span>
            </div>
          </template>

          <template v-slot:item.generation="{ item }">
            <div class="ctc-articles__generation">
              <template v-if="item.generation.kind === 'queued'">
                <span class="ctc-articles__site">
                  {{ item.generation.label }}
                </span>
                <router-link
                  v-if="item.aifRoute && $can('read', 'ai_factory')"
                  :to="item.aifRoute"
                  class="ctc-articles__aif-link"
                >
                  Voir la génération AIF #{{ item.generation.aifQueueId }}
                  <v-icon x-small color="currentColor">
                    {{ icons.mdiOpenInNew }}
                  </v-icon>
                </router-link>
              </template>
              <template v-else>
                <rmr-soft-badge
                  :label="item.generation.label"
                  :icon="
                    item.generation.kind === 'waiting'
                      ? icons.mdiClockOutline
                      : icons.mdiAlertCircleOutline
                  "
                  :color="
                    item.generation.kind === 'waiting'
                      ? colors.muted
                      : colors.issue
                  "
                  :background="
                    item.generation.kind === 'waiting'
                      ? colors.mutedBackground
                      : colors.issueBackground
                  "
                  size="xs"
                />
                <small v-if="item.generation.message">
                  {{ item.generation.message }}
                </small>
              </template>
              <small
                v-for="warning in item.generationWarnings"
                :key="warning"
                class="ctc-articles__generation-warning"
              >
                {{ warning }}
              </small>
            </div>
          </template>

          <template v-slot:item.updatedAt="{ item }">
            <div class="ctc-articles__date">
              {{ formatDateTime(item.updatedAt) }}
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="rmr-data-table__actions ctc-articles__actions">
              <span
                v-if="item.issue"
                class="ctc-articles__issue-copy"
                role="status"
              >
                <rmr-soft-badge
                  :label="item.issue.label"
                  :icon="icons.mdiAlertCircleOutline"
                  :color="colors.issue"
                  :background="colors.issueBackground"
                  size="xs"
                />
                <small v-if="item.issue.reason">{{ item.issue.reason }}</small>
              </span>
              <rmr-action-button
                v-if="item.issue && item.issue.retryable"
                label="Réessayer"
                :icon="icons.mdiRefresh"
                variant="utility"
                size="lg"
                :color="colors.issue"
                :loading="retryingIds.includes(item.id)"
                @click="$emit('retry-affilizz', item.id)"
              />
              <rmr-action-button
                v-if="!item.issue && item.generation.reschedulable"
                label="Reprogrammer"
                :icon="icons.mdiCalendarClock"
                variant="utility"
                size="lg"
                :color="colors.issue"
                :loading="reschedulingIds.includes(item.id)"
                @click="$emit('reschedule', item.id)"
              />
              <rmr-action-button
                v-else-if="!item.issue && item.generation.retryable"
                label="Relancer"
                :icon="icons.mdiRefresh"
                variant="utility"
                size="lg"
                :color="colors.issue"
                :loading="retryingIds.includes(item.id)"
                :title="
                  item.generation.message ||
                  'Revérifie Affilizz et transmet l’article à la génération'
                "
                @click="$emit('retry-affilizz', item.id)"
              />
            </div>
          </template>

          <template v-slot:no-data>
            <div class="rmr-data-table__state">
              <div class="rmr-data-table__state-title">
                {{ search ? "Aucun résultat" : "Aucun brouillon" }}
              </div>
              <div class="rmr-data-table__state-text">
                {{
                  search
                    ? "Aucun brouillon ne correspond à cette recherche."
                    : "Créez un brouillon depuis « Créer un article »."
                }}
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  computed,
  onBeforeUnmount,
  reactive,
  ref,
  watch,
} from "@vue/composition-api";
import {
  mdiAlertCircleOutline,
  mdiCalendarClock,
  mdiClockOutline,
  mdiMagnify,
  mdiOpenInNew,
  mdiRefresh,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrSoftBadge from "@/components/ui/feedback/RmrSoftBadge.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrTableHeader from "@/components/ui/layout/RmrTableHeader.vue";
import {
  RMR_CA_DARK,
  RMR_CA_SOFT,
  RMR_SURFACE_2,
  RMR_TEXT_SECONDARY,
} from "@/styles/tokens";
import { URL_PARAM_NAMES } from "@/utils/constants";

import {
  affilizzIssueOf,
  aifGenerationRoute,
  formatDateTime,
  generationStateOf,
  merchantLabel,
  requestedPublicationLabel,
} from "../ctc-model";

const HEADERS = [
  { text: "Produit", value: "title", width: "25%", sortable: false },
  { text: "Site", value: "siteName", width: "10%", sortable: false },
  {
    text: "Publication demandée",
    value: "postStatus",
    width: "14%",
    sortable: false,
  },
  { text: "Génération", value: "generation", width: "24%", sortable: false },
  { text: "Mis à jour", value: "updatedAt", width: "9%", sortable: false },
  {
    text: "Action",
    value: "actions",
    sortable: false,
    filterable: false,
    align: "end",
    width: "18%",
  },
];

export default {
  name: "CtcArticlesView",
  components: {
    RmrActionButton,
    RmrSoftBadge,
    RmrTableHeader,
    RmrTextField,
  },
  props: {
    articles: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    page: {
      type: Number,
      default: 1,
    },
    limit: {
      type: Number,
      default: 10,
    },
    highlightedId: {
      type: [Number, String],
      default: null,
    },
    retryingIds: {
      type: Array,
      default: () => [],
    },
    reschedulingIds: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const search = ref("");
    const options = reactive({
      page: props.page,
      itemsPerPage: props.limit,
      sortBy: [],
      sortDesc: [],
    });
    // Not :options.sync: it would replace `options` on the instance while the
    // watchers below keep observing this object, so no page would ever load.
    const setOptions = (value) => Object.assign(options, value);
    let searchTimer = null;
    const load = () =>
      emit("load", {
        page: options.page,
        limit: options.itemsPerPage,
        search: (search.value || "").trim() || undefined,
      });

    watch(
      () => [options.page, options.itemsPerPage],
      ([page, limit], [previousPage, previousLimit]) => {
        if (page === previousPage && limit === previousLimit) return;
        load();
      }
    );
    watch(search, () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        if (options.page !== 1) options.page = 1;
        else load();
      }, 300);
    });
    onBeforeUnmount(() => clearTimeout(searchTimer));

    const rows = computed(() =>
      props.articles.map((article) => ({
        id: article.id,
        title: article.productTitle,
        merchant: merchantLabel(article.merchant),
        signature: article.signature,
        siteName: article.siteName || `Site ${article.siteId}`,
        siteDomain: article.siteDomain || "",
        postStatusLabel: requestedPublicationLabel(
          article.postStatus,
          article.scheduledAt
        ),
        issue: affilizzIssueOf(article),
        siteId: article.siteId,
        generation: generationStateOf(article),
        generationWarnings: (article.generation?.warnings || []).slice(),
        aifRoute: aifGenerationRoute(article, URL_PARAM_NAMES.Search),
        updatedAt: article.updatedAt,
      }))
    );

    return {
      setOptions,
      colors: {
        issue: RMR_CA_DARK,
        issueBackground: RMR_CA_SOFT,
        muted: RMR_TEXT_SECONDARY,
        mutedBackground: RMR_SURFACE_2,
      },
      formatDateTime,
      headers: HEADERS,
      icons: {
        mdiAlertCircleOutline,
        mdiCalendarClock,
        mdiClockOutline,
        mdiMagnify,
        mdiOpenInNew,
        mdiRefresh,
      },
      options,
      rowClass: (item) =>
        item.id === props.highlightedId ? "ctc-articles__row--highlight" : "",
      rows,
      search,
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-articles__shell {
  overflow: hidden;
}

.ctc-articles__count {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: $rmr-radius-pill;
  background: $rmr-indigo-bg;
  color: $rmr-indigo;
  font-size: $rmr-fs-sm;
}

.ctc-articles__heading-help {
  margin-top: 4px;
  color: $rmr-text-muted;
  font-size: 10px;
  font-weight: 500;
}

.ctc-articles__article-copy {
  min-width: 0;
}

.ctc-articles__title {
  display: -webkit-box;
  overflow: hidden;
  color: $rmr-text-primary;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.ctc-articles__product,
.ctc-articles__muted {
  color: $rmr-text-muted;
  font-size: 10px;
}

.ctc-articles__product {
  overflow: hidden;
  margin-top: 3px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ctc-articles__site {
  color: $rmr-text-primary;
  font-size: 11px;
  font-weight: 750;
}

.ctc-articles__workflow {
  display: grid;
  justify-items: start;
  gap: 5px;
}

.ctc-articles__date {
  color: $rmr-text-medium;
  font-size: 10px;
  white-space: nowrap;
}

.ctc-articles__aif-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: $rmr-indigo;
  font-size: 11px;
  font-weight: 750;
  text-decoration: none;
  white-space: nowrap;
}

.ctc-articles__aif-link:hover {
  text-decoration: underline;
}

.ctc-articles__actions {
  min-width: 160px;
  gap: 8px;
}

.ctc-articles__issue-copy {
  display: grid;
  min-width: 0;
  justify-items: end;
  gap: 4px;
  text-align: right;
}

.ctc-articles__generation {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.ctc-articles__generation small {
  max-width: 260px;
  color: $rmr-text-muted;
  font-size: $rmr-fs-sm;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.ctc-articles__generation .ctc-articles__generation-warning {
  color: $rmr-orange;
}

.ctc-articles__issue-copy small {
  max-width: 260px;
  color: $rmr-text-muted;
  font-size: $rmr-fs-sm;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.ctc-articles__table ::v-deep {
  thead th {
    height: 46px;
    background: $rmr-surface-1;
    color: $rmr-text-muted !important;
    font-size: 9px !important;
    font-weight: 850 !important;
    letter-spacing: 0.055em;
    text-transform: uppercase;
  }

  tbody td {
    height: 76px;
    border-bottom-color: $rmr-border !important;
  }

  tbody tr:hover {
    background: #fafbff !important;
  }

  .ctc-articles__row--highlight {
    animation: ctc-row-highlight 3s ease-out;
  }
}

@keyframes ctc-row-highlight {
  0%,
  60% {
    background-color: $rmr-indigo-bg;
  }

  100% {
    background-color: transparent;
  }
}

@media (max-width: 599px) {
  .ctc-articles__actions {
    min-width: 0;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}
</style>
